import React from "react";
import { Mail, Phone, User } from "lucide-react";

/**
 * Shared tabular display for Governing Body / Executive Body members on public sites.
 * Columns: # | Photo | Full Name | Designation | Current Profession | Email | Mobile
 */
export default function BodyMembersTable({
  members = [],
  theme = "amber",
  testidPrefix = "gb",
}) {
  const THEMES = {
    amber: { accent: "#D48F1A", border: "border-amber-200", soft: "bg-amber-50" },
    navy: { accent: "#D4A017", border: "border-slate-200", soft: "bg-[#001F3F]/5" },
    cream: { accent: "#AF734A", border: "border-[#E5D9C6]", soft: "bg-[#FBF6EE]" },
    saffron: { accent: "#FF7B00", border: "border-[#F0E2C0]", soft: "bg-[#FFF1DC]" },
    cyan: { accent: "#06B6D4", border: "border-cyan-200", soft: "bg-cyan-50" },
    emerald: { accent: "#0FB28A", border: "border-emerald-200", soft: "bg-emerald-50" },
    yellow: { accent: "#FDC700", border: "border-yellow-200", soft: "bg-yellow-50" },
    orange: { accent: "#F4751F", border: "border-orange-200", soft: "bg-orange-50" },
  };
  const t = THEMES[theme] || THEMES.amber;
  const active = (members || []).filter((m) => (m.status || "Active") === "Active");

  if (active.length === 0) {
    return (
      <div className={`bg-white border ${t.border} rounded-lg p-12 text-center`} data-testid={`${testidPrefix}-empty`}>
        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${t.accent}22`, color: t.accent }}>
          <User size={22} strokeWidth={1.5} />
        </div>
        <div className="mt-3 text-slate-600">Members will appear here once published by the institute.</div>
      </div>
    );
  }

  return (
    <div className={`bg-white border ${t.border} rounded-lg overflow-hidden`} data-testid={`${testidPrefix}-table`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 text-[11px] uppercase tracking-wider" style={{ background: `${t.accent}10` }}>
              <th className="px-4 py-3 w-16">Sr. No</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Designation</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {active.map((m, i) => (
              <tr key={m.id} data-testid={`${testidPrefix}-row-${m.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-500 font-medium align-top">{i + 1}</td>
                <td className="px-4 py-3 font-semibold text-slate-900 align-top">
                  {m.full_name}
                  {m.current_profession && (
                    <span className="font-normal text-slate-600"> ({m.current_profession})</span>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  {m.designation ? (
                    <span className="text-[11px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded-sm" style={{ background: `${t.accent}22`, color: t.accent }}>
                      {m.designation}
                    </span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
